import { CONSTANTS } from "../data";

// A simplified representation of a user action for tutorial validation.
interface TutorialAction {
  type: 'selectItem' | 'brew' | 'serve';
  itemId?: string;
  ingredients?: string[];
}

// The relevant parts of the tutorial state from the main game state.
interface TutorialState {
  isTutorialActive: boolean;
  currentTutorial: {
    id: string;
    step: number;
  } | null;
}

/**
 * Checks if a given action is permitted by the current tutorial step.
 * @param action The proposed action the user is trying to perform.
 * @param tutorialState The current tutorial state from the game.
 * @returns {boolean} True if the action is allowed, false otherwise.
 */
export const isActionAllowedInTutorial = (
  action: TutorialAction,
  tutorialState: TutorialState
): boolean => {
  if (!tutorialState.isTutorialActive || !tutorialState.currentTutorial) {
    // No tutorial is active, so all actions are permitted.
    return true;
  }

  const tutorialDef = CONSTANTS.TUTORIALS[tutorialState.currentTutorial.id];
  if (!tutorialDef) {
    console.warn(`Tutorial definition not found for ${tutorialState.currentTutorial.id}`);
    // Failsafe: if tutorial definition is missing, allow action.
    return true;
  }

  const step = tutorialDef.steps[tutorialState.currentTutorial.step];
  if (!step || !step.action) {
    // This is an informational step with no required action, so permit any action.
    return true;
  }

  const required = step.action;

  // For selectItem actions, we need to be more permissive to allow tutorial progression
  if (action.type === 'selectItem') {
    // If the tutorial requires selecting a specific item, only allow that item
    if (required.type === 'selectItem') {
      return action.itemId === required.itemId;
    }
    // If the tutorial requires a different action (brew, serve), allow any item selection
    // This allows players to select items to prepare for the required action
    return true;
  }

  // For non-selectItem actions, be strict about matching the required action
  if (action.type !== required.type) {
    return false;
  }

  switch (required.type) {
    case "brew":
      if (!Array.isArray(required.ingredients) || !Array.isArray(action.ingredients)) {
        return false;
      }
      // Sort ingredients to ensure order doesn't matter.
      const reqSorted = [...required.ingredients].sort().join("_");
      const actSorted = [...action.ingredients].sort().join("_");
      return reqSorted === actSorted;
    case "serve":
      return action.itemId === required.orderId;
    default:
      // Unknown required action type.
      return false;
  }
};
